import React from "react";
import { Phone, Mail, MapPin, LoaderPinwheel, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact" ref={ref} className="relative bg-slate-950 py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20 pointer-events-none" />

      <div className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="space-y-10 text-slate-300">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Давайте начнём</h2>
              <p className="text-lg text-slate-400 max-w-md leading-relaxed">
                Готовы повысить присутствие вашего ритейла? Наши эксперты готовы обсудить вашу стратегию мерчандайзинга.
              </p>
            </div>

            <div className="space-y-8">
              <div className="space-y-2">
                <p className="text-sm font-medium text-primary uppercase tracking-wider">Прямой телефон</p>
                <Link to="tel:+7(861)203-39-43" className="block text-3xl md:text-4xl font-bold text-white hover:text-primary transition-colors">
                  +7(861)203-39-43
                </Link>
                <p className="text-sm text-slate-500">Пн‑Пт: 9:00–18:00</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4 group">
                  <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 group-hover:border-primary/50 transition-colors">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Посетите наш офис</h4>
                    <p className="text-slate-400">350011, Краснодарский край, г Краснодар, Старокубанская ул, д. 2/15, помещ. 3</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 group-hover:border-primary/50 transition-colors">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Напишите нам</h4>
                    <Link to="mailto:agency.profi.krd@yandex.ru" className="text-slate-400 hover:text-white transition-colors">
                      agency.profi.krd@yandex.ru
                    </Link>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {}
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 md:p-8 lg:p-10 shadow-2xl shadow-black/20">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Отправить сообщение</h3>
              <p className="text-slate-400 text-sm">Заполните форму ниже, и мы ответим вам в течение 24 часов.</p>
            </div>

            <FormProvider formId="contact_form_home" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            message: "",
            is_new_client: true
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Отправить сообщение" successMessage="Спасибо! Ваше сообщение успешно отправлено.">
              <ContactForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-2">
          <div className="w-8 h-8 text-green-500"></div>
        </div>
        <h3 className="text-2xl font-bold text-white">Сообщение отправлено!</h3>
        <p className="text-slate-400 max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-300">Ваше имя <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Иван Иванов" {...field} className="bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus-visible:border-primary focus-visible:ring-primary/20 h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-300">Электронная почта <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="ivan@example.com" {...field} className="bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus-visible:border-primary focus-visible:ring-primary/20 h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-300">Номер телефона</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+7 (999) 123-45-67" {...field} className="bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus-visible:border-primary focus-visible:ring-primary/20 h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-300">Компания</FormLabel>
              <FormControl>
                <Input placeholder="MerchExpert ООО" {...field} className="bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus-visible:border-primary focus-visible:ring-primary/20 h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="is_new_client" render={({
      field
    }) => <FormItem className="flex flex-row items-center justify-between rounded-lg border border-slate-800 bg-slate-950/30 p-4">
            <div className="space-y-0.5">
              <FormLabel className="text-base text-slate-200">
                {field.value ? "Новый клиент" : "Существующий клиент"}
              </FormLabel>
              <p className="text-xs text-slate-500">
                Вы сейчас работаете с нами?
              </p>
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} className="data-[state=checked]:bg-primary" />
            </FormControl>
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-300">Сообщение <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Расскажите о вашем проекте или запросе..." {...field} className="bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus-visible:border-primary focus-visible:ring-primary/20 min-h-[120px] resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold text-lg shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300 animate-pulse hover:animate-none">
        {isSubmitting ? <>
            <LoaderPinwheel className="animate-spin mr-2 h-5 w-5" />
            Sending...
          </> : <>
            Отправить сообщение
            <ArrowRight className="ml-2 h-5 w-5" />
          </>}
      </Button>
    </Form>;
}